(() => {
  if (customElements.get('otf-brand-sign')) return;
  class OtfBrandSign extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('button');
      this.drawer = this.closest('#menu-drawer');
      if (!this.video || !this.toggle || !this.drawer) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.visible = false;
      this.finished = false;
      this.userPaused = false;
      this.failed = false;
      this.token = 0;
      this.sync = this.sync.bind(this);
      this.onVisibility = () => this.sync();
      this.onPreference = () => {
        if (this.motion.matches || this.connection?.saveData) this.showPoster();
        this.sync();
      };
      this.onToggle = () => {
        if (this.failed || this.motion.matches || this.connection?.saveData || !this.canShow()) return;
        if (!this.video.paused) {
          this.userPaused = true;
          this.stop();
        } else {
          this.userPaused = false;
          if (this.finished) { this.finished = false; this.video.currentTime = 0; }
          this.start();
        }
      };
      this.onPlaying = () => {
        if (this.motion.matches || this.connection?.saveData) { this.showPoster(); return; }
        if (!this.canShow()) { this.stop(); return; }
        this.setAttribute('data-frame-ready', '');
        this.setAttribute('data-playing', '');
        this.toggle.setAttribute('aria-label', this.toggle.dataset.pauseLabel);
        this.toggle.setAttribute('aria-pressed', 'true');
      };
      this.onPause = () => {
        this.removeAttribute('data-playing');
        this.toggle.setAttribute('aria-label', this.toggle.dataset.playLabel);
        this.toggle.setAttribute('aria-pressed', 'false');
      };
      this.onEnded = () => { this.finished = true; this.onPause(); };
      this.onError = () => { this.failed = true; this.showPoster(); this.toggle.hidden = true; };
      this.toggle.addEventListener('click', this.onToggle);
      this.video.addEventListener('playing', this.onPlaying);
      this.video.addEventListener('pause', this.onPause);
      this.video.addEventListener('ended', this.onEnded);
      this.video.addEventListener('error', this.onError);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener?.('change', this.onPreference);
      this.connection?.addEventListener?.('change', this.onPreference);
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.drawer, { attributes: true, subtree: true, attributeFilter: ['class', 'style', 'inert', 'aria-hidden', 'aria-expanded', 'hidden'] });
      this.observer.observe(document.body, { attributes: true, attributeFilter: ['active-popup'] });
      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.6;
          this.sync();
        }, { threshold: [0, 0.6, 1] });
        this.intersection.observe(this);
      }
      this.sync();
    }
    canShow() {
      return this.isConnected && this.visible && !document.hidden &&
        document.body.getAttribute('active-popup') === 'mobile-navigation' &&
        !this.drawer.classList.contains('hide-popup') && this.drawer.getAttribute('aria-hidden') === 'false' &&
        !this.closest('[inert]') && (!this.checkVisibility || this.checkVisibility({ checkVisibilityCSS: true, checkOpacity: true }));
    }
    sync() {
      const allowed = this.canShow() && !this.motion.matches && !this.connection?.saveData && !this.failed;
      if (this.toggle.hidden !== !allowed) this.toggle.hidden = !allowed;
      if (!allowed || this.finished || this.userPaused) { this.stop(); return; }
      this.start();
    }
    async start() {
      if (this.pending || !this.video.paused || !this.canShow() || this.motion.matches || this.connection?.saveData || this.failed) return;
      const token = ++this.token;
      this.pending = true;
      this.video.muted = true;
      if (!this.video.getAttribute('src')) this.video.src = this.video.dataset.src;
      try {
        await this.video.play();
        if (this.motion.matches || this.connection?.saveData) this.showPoster();
        else if (token !== this.token || !this.canShow()) this.stop();
      } catch {
        if (token === this.token) { this.userPaused = true; this.showPoster(); }
      } finally {
        this.pending = false;
        if (token !== this.token && this.canShow() && !this.motion.matches && !this.connection?.saveData && !this.userPaused && !this.finished && !this.failed) this.sync();
      }
    }
    stop() { this.token += 1; this.video.pause(); this.onPause(); }
    showPoster() { this.stop(); this.removeAttribute('data-frame-ready'); }
    disconnectedCallback() {
      if (!this.video) return;
      this.stop();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      this.toggle.removeEventListener('click', this.onToggle);
      this.video.removeEventListener('playing', this.onPlaying);
      this.video.removeEventListener('pause', this.onPause);
      this.video.removeEventListener('ended', this.onEnded);
      this.video.removeEventListener('error', this.onError);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.motion?.removeEventListener?.('change', this.onPreference);
      this.connection?.removeEventListener?.('change', this.onPreference);
      this.ready = false;
    }
  }
  customElements.define('otf-brand-sign', OtfBrandSign);
})();
